import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AppWindow, ArrowLeft, ChevronRight, ExternalLink, KeyRound, Pencil, Plus } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { CopyButton, KV } from '@/components/catalog/CatalogParts';
import {
  AnnualOptionBadge,
  NeedsPricingBadge,
  RenewalBadge,
  SoftwareStatusBadge,
} from '@/components/catalog/SoftwareBadges';
import { SoftwareDialog } from '@/components/catalog/SoftwareDialog';
import { SoftwareSeatDialog } from '@/components/catalog/SoftwareSeatDialog';
import { initialsOf } from '@/components/projects/PeoplePicker';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { MobileListCard, MobileListCardStack } from '@/components/ui/MobileListCard';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { useAuth } from '@/app/AuthProvider';
import { useSoftwareCatalog, type SoftwareSeat } from '@/features/software/hooks';
import { useOnlineAccounts } from '@/features/onlineAccounts/hooks';
import {
  BILLING_CYCLE_LABELS,
  SOFTWARE_CATEGORY_LABELS,
  durationSince,
  seatHolderName,
  websiteHref,
  websiteLabel,
  withCurrentProfile,
  type SeatProfile,
  type SoftwareRow,
} from '@/features/software/model';
import { formatCurrency, formatDateOnly } from '@/lib/formatters';
import { canWrite } from '@/lib/permissions';
import { useIsMobile } from '@/lib/useMediaQuery';

/** One software subscription: what it costs, what it includes, and who has a seat. */
export function SoftwareDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { role } = useAuth();
  const canEdit = canWrite(role, 'catalog');
  const { rows, profilesById, assignable, isLoading, error } = useSoftwareCatalog();
  const [editing, setEditing] = useState(false);
  const [seatDialog, setSeatDialog] = useState<{ seat: SoftwareSeat | null } | null>(null);

  const row = rows.find((r) => r.id === id) ?? null;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <BackLink />
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="h-72 lg:col-span-2" />
          <Skeleton className="h-72" />
        </div>
      </div>
    );
  }

  if (error || !row) {
    return (
      <div className="space-y-6">
        <BackLink />
        <EmptyState
          icon={AppWindow}
          title={error ? 'Could not load this software' : 'Software not found'}
          detail={error instanceof Error ? error.message : undefined}
          action={
            <Button asChild variant="outline">
              <Link to="/catalog/software/subscriptions">Back to subscriptions</Link>
            </Button>
          }
        />
      </div>
    );
  }

  const takenProfileIds = new Set(
    row.seats.map((s) => s.profile_id).filter((v): v is string => !!v),
  );
  const editingSeat = seatDialog?.seat ?? null;

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <BackLink />
        <PageHeader
          className="pb-0"
          eyebrow="Catalog · Software"
          title={row.name}
          meta={
            <span className="flex flex-wrap items-center gap-2">
              {(row.vendor || row.plan_name) && (
                <span>{[row.vendor, row.plan_name].filter(Boolean).join(' · ')}</span>
              )}
              <Badge variant="secondary">{SOFTWARE_CATEGORY_LABELS[row.category]}</Badge>
              <SoftwareStatusBadge status={row.status} />
            </span>
          }
          actions={
            canEdit ? (
              <Button variant="outline" onClick={() => setEditing(true)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
            ) : undefined
          }
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <SeatsCard
            row={row}
            profilesById={profilesById}
            canEdit={canEdit}
            onAdd={() => setSeatDialog({ seat: null })}
            onOpen={(seat) => setSeatDialog({ seat })}
          />
          <Card>
            <CardHeader>
              <CardTitle>What we get</CardTitle>
            </CardHeader>
            <CardContent>
              {row.includes ? (
                <p className="whitespace-pre-wrap text-sm leading-body text-foreground">
                  {row.includes}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Nothing noted yet.
                  {canEdit && (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="text-atmosphere-dark underline-offset-4 hover:underline"
                        onClick={() => setEditing(true)}
                      >
                        Add what the plan includes
                      </button>
                    </>
                  )}
                </p>
              )}
            </CardContent>
          </Card>
          {row.notes && (
            <Card>
              <CardHeader>
                <CardTitle>Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap text-sm leading-body text-foreground">
                  {row.notes}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <CostCard row={row} canEdit={canEdit} onEdit={() => setEditing(true)} />
          <AccountCard row={row} />
          <LinkedAccountsCard subscriptionId={row.id} />
          <DatesCard row={row} />
        </div>
      </div>

      <SoftwareDialog
        open={editing}
        subscription={row}
        assignedSeats={row.seats.length}
        profiles={withCurrentProfile(assignable, row.owner_id, profilesById)}
        canEdit={canEdit}
        onOpenChange={setEditing}
        onDeleted={() => navigate('/catalog/software/subscriptions')}
      />
      <SoftwareSeatDialog
        open={seatDialog !== null}
        seat={editingSeat}
        subscriptionId={row.id}
        subscriptionName={row.name}
        profiles={withCurrentProfile(assignable, editingSeat?.profile_id, profilesById)}
        takenProfileIds={takenProfileIds}
        canEdit={canEdit}
        onOpenChange={(open) => {
          if (!open) setSeatDialog(null);
        }}
      />
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/catalog/software/subscriptions"
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
      Back to subscriptions
    </Link>
  );
}

function CostCard({
  row,
  canEdit,
  onEdit,
}: {
  row: SoftwareRow;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const { costs } = row;
  const seatsBilled =
    costs.billedSeats == null
      ? null
      : `${costs.billedSeats}${row.seat_count == null ? ' (assigned)' : ''}`;
  const savingsPct =
    costs.annualSavingsCents != null && costs.yearlyCents
      ? Math.round((costs.annualSavingsCents / costs.yearlyCents) * 100)
      : null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle>Cost</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {row.price_cents == null ? (
          <div className="space-y-3">
            <NeedsPricingBadge />
            <p className="text-sm text-muted-foreground">
              Add the plan price so this counts toward monthly spend.
            </p>
            {canEdit && (
              <Button size="sm" variant="outline" onClick={onEdit}>
                Add pricing
              </Button>
            )}
          </div>
        ) : (
          <>
            <div>
              <div className="text-2xl font-medium tabular-nums tracking-headline text-foreground">
                {row.billing_cycle === 'one_time'
                  ? formatCurrency(costs.perBillCents)
                  : formatCurrency(costs.monthlyCents)}
              </div>
              <div className="text-xs text-muted-foreground">
                {row.billing_cycle === 'one_time' ? 'One-time purchase' : 'per month'}
              </div>
            </div>
            <div className="space-y-2">
              <KV
                label="Billed"
                value={`${formatCurrency(row.price_cents)}${row.price_per_seat ? ' per seat' : ''}, ${BILLING_CYCLE_LABELS[row.billing_cycle].toLowerCase()}`}
              />
              {seatsBilled && <KV label="Seats billed" value={seatsBilled} />}
              {costs.perBillCents != null &&
                row.billing_cycle !== 'monthly' &&
                row.billing_cycle !== 'one_time' && (
                  <KV label="Each bill" value={formatCurrency(costs.perBillCents)} />
                )}
              {costs.yearlyCents != null && (
                <KV label="Per year" value={formatCurrency(costs.yearlyCents)} />
              )}
            </div>
          </>
        )}

        <div className="space-y-2 border-t border-border pt-4">
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-muted-foreground">Annual plan</span>
            <AnnualOptionBadge state={row.annualState} savingsCents={costs.annualSavingsCents} />
          </div>
          {costs.annualPlanCents != null && (
            <KV label="Annual price" value={`${formatCurrency(costs.annualPlanCents)} / yr`} />
          )}
          {costs.annualSavingsCents != null && (
            <p className="text-sm text-muted-foreground">
              {costs.annualSavingsCents > 0 ? (
                <>
                  Switching saves{' '}
                  <span className="font-medium text-bright-moss-dark">
                    {formatCurrency(costs.annualSavingsCents)} a year
                  </span>
                  {savingsPct ? ` (${savingsPct}%)` : ''}.
                </>
              ) : costs.annualSavingsCents < 0 ? (
                `The annual plan costs ${formatCurrency(Math.abs(costs.annualSavingsCents))} more a year.`
              ) : (
                'The annual plan costs the same.'
              )}
            </p>
          )}
          {row.annualState === 'unknown' && (
            <p className="text-sm text-muted-foreground">
              Check whether {row.vendor || 'the vendor'} offers a cheaper annual plan.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function AccountCard({ row }: { row: SoftwareRow }) {
  const href = websiteHref(row.website);
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle>Account</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <KV
          label="Login"
          value={
            row.account_login ? (
              <span className="inline-flex max-w-full items-center gap-1">
                <span className="truncate">{row.account_login}</span>
                <CopyButton value={row.account_login} label="Login" />
              </span>
            ) : (
              <span className="text-muted-foreground">—</span>
            )
          }
        />
        <KV
          label="Sign in"
          value={
            href && row.website ? (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex max-w-full items-center gap-1 text-atmosphere-dark hover:underline"
              >
                <span className="truncate">{websiteLabel(row.website)}</span>
                <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              </a>
            ) : (
              <span className="text-muted-foreground">—</span>
            )
          }
        />
        <KV
          label="Owner"
          value={row.ownerName ?? <span className="text-muted-foreground">—</span>}
        />
        <KV
          label="Paid with"
          value={row.payment_method ?? <span className="text-muted-foreground">—</span>}
        />
      </CardContent>
    </Card>
  );
}

/** Online accounts linked to this subscription that the viewer has access to. */
function LinkedAccountsCard({ subscriptionId }: { subscriptionId: string }) {
  const { data } = useOnlineAccounts();
  const linked = (data ?? []).filter((a) => a.subscription_id === subscriptionId);
  if (linked.length === 0) return null;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle>Logins & passwords</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        {linked.map((account) => (
          <Link
            key={account.id}
            to={`/catalog/software/accounts/${account.id}`}
            className="-mx-2 flex min-h-11 items-center gap-2 rounded-md px-2 text-sm text-foreground hover:bg-secondary"
          >
            <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate">{account.name}</span>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          </Link>
        ))}
      </CardContent>
    </Card>
  );
}

function DatesCard({ row }: { row: SoftwareRow }) {
  const tenure = durationSince(row.started_on);
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle>Dates</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <KV
          label="Started"
          value={
            row.started_on ? (
              <span>
                {formatDateOnly(row.started_on)}
                {tenure && <span className="block text-xs text-muted-foreground">{tenure}</span>}
              </span>
            ) : (
              <span className="text-muted-foreground">—</span>
            )
          }
        />
        <KV
          label="Next renewal"
          value={
            row.renews_on ? (
              <span className="inline-flex items-center gap-2">
                <RenewalBadge renewsOn={row.renews_on} status={row.status} />
                {formatDateOnly(row.renews_on)}
              </span>
            ) : (
              <span className="text-muted-foreground">—</span>
            )
          }
        />
        <KV label="Auto-renews" value={row.auto_renews ? 'Yes' : 'No'} />
      </CardContent>
    </Card>
  );
}

function SeatsCard({
  row,
  profilesById,
  canEdit,
  onAdd,
  onOpen,
}: {
  row: SoftwareRow;
  profilesById: Map<string, SeatProfile>;
  canEdit: boolean;
  onAdd: () => void;
  onOpen: (seat: SoftwareSeat) => void;
}) {
  const isMobile = useIsMobile();
  const seats = useMemo(
    () =>
      [...row.seats].sort((a, b) =>
        seatHolderName(a, profilesById).localeCompare(seatHolderName(b, profilesById)),
      ),
    [row.seats, profilesById],
  );
  const over = row.seat_count != null ? row.seats.length - row.seat_count : 0;
  const open = canEdit ? onOpen : undefined;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="space-y-1">
          <CardTitle>Seats & logins</CardTitle>
          <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            {row.seat_count != null
              ? `${row.seats.length} of ${row.seat_count} seats assigned`
              : `${row.seats.length} ${row.seats.length === 1 ? 'seat' : 'seats'} assigned`}
            {over > 0 && <Badge variant="warning-soft">{over} over the plan</Badge>}
          </p>
        </div>
        {canEdit && (
          <Button size="sm" onClick={onAdd}>
            <Plus className="h-4 w-4" />
            Add seat
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {seats.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
            No one has a seat yet.
          </p>
        ) : isMobile ? (
          <MobileListCardStack>
            {seats.map((seat) => {
              const profile = seat.profile_id ? profilesById.get(seat.profile_id) : undefined;
              return (
                <MobileListCard
                  key={seat.id}
                  leading={
                    <SeatAvatar
                      seat={seat}
                      profile={profile}
                      name={seatHolderName(seat, profilesById)}
                    />
                  }
                  title={seatHolderName(seat, profilesById)}
                  subtitle={seat.login ?? 'No login recorded'}
                  badges={[
                    seat.license_type ? (
                      <Badge key="license" variant="secondary">
                        {seat.license_type}
                      </Badge>
                    ) : null,
                    !seat.profile_id ? (
                      <Badge key="outside" variant="outline">
                        Not in Atlas
                      </Badge>
                    ) : null,
                  ]}
                  meta={
                    seat.assigned_on
                      ? [{ label: 'Since', value: formatDateOnly(seat.assigned_on) }]
                      : undefined
                  }
                  onTap={open ? () => open(seat) : undefined}
                  trailing={open ? undefined : null}
                />
              );
            })}
          </MobileListCardStack>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Person</TableHead>
                <TableHead>Login</TableHead>
                <TableHead>License or role</TableHead>
                <TableHead>Since</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {seats.map((seat) => {
                const profile = seat.profile_id ? profilesById.get(seat.profile_id) : undefined;
                const name = seatHolderName(seat, profilesById);
                return (
                  <TableRow
                    key={seat.id}
                    className={open ? 'cursor-pointer' : undefined}
                    onClick={open ? () => open(seat) : undefined}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <SeatAvatar seat={seat} profile={profile} name={name} />
                        <div className="min-w-0">
                          <div className="truncate text-foreground">{name}</div>
                          {!seat.profile_id ? (
                            <div className="text-xs text-muted-foreground">Not in Atlas</div>
                          ) : seat.notes ? (
                            <div className="truncate text-xs text-muted-foreground">
                              {seat.notes}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {seat.login ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="truncate">{seat.login}</span>
                          <CopyButton value={seat.login} label="Login" />
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {seat.license_type ?? <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {seat.assigned_on ? (
                        formatDateOnly(seat.assigned_on)
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function SeatAvatar({
  seat,
  profile,
  name,
}: {
  seat: SoftwareSeat;
  profile: SeatProfile | undefined;
  name: string;
}) {
  return (
    <Avatar className="h-8 w-8">
      {profile?.avatar_url && <AvatarImage src={profile.avatar_url} alt="" />}
      <AvatarFallback className="text-[10px]">
        {initialsOf({ id: seat.id, full_name: name, email: profile?.email ?? name })}
      </AvatarFallback>
    </Avatar>
  );
}
